import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, User, FileText, Plus, Trash2, X, Phone, Building2 } from 'lucide-react';
import { appointmentsAPI, designationsAPI, usersAPI } from '../../../lib/api.js';
import { getUser } from '../../../lib/auth.js';
import { useCompanies } from '../../../hooks/useSharedData.js';
import Card from '../../../components/ui/Card.jsx';
import Button from '../../../components/ui/Button.jsx';
import Input from '../../../components/ui/Input.jsx';
import Badge from '../../../components/ui/Badge.jsx';
import RichTextEditor from '../../../components/ui/RichTextEditor.jsx';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

export const LeaveAppointmentForm = ({ onClose, onSuccess, editAppointment, currentUser }) => {
  // memoize resolved user context so it doesn't change identity every render
  const userCtx = useMemo(() => currentUser ?? getUser() ?? {}, [currentUser]);

  // Determine gates by designation first (fallback to role)
  const designationId = userCtx?.designation_id || userCtx?.designationId;
  const { data: designationResp } = useQuery({
    queryKey: ['designation', designationId],
    queryFn: () => designationsAPI.getById(designationId),
    enabled: !!designationId,
    staleTime: 5 * 60 * 1000,
  });
  const resolvedDesignationName = (
    userCtx?.designation_name ||
    userCtx?.designation?.name ||
    designationResp?.data?.name ||
    designationResp?.data?.data?.name ||
    ''
  ).toLowerCase();
  const roleStr = (userCtx?.role || userCtx?.role_name || '').toLowerCase();
  const isClerk = resolvedDesignationName.includes('clerk') || roleStr.includes('clerk');
  const isOfficer = resolvedDesignationName.includes('officer') || roleStr.includes('officer');
  const isCommandant =
    resolvedDesignationName.includes('commandant') ||
    resolvedDesignationName.includes('executive') ||
    roleStr.includes('commandant') ||
    roleStr.includes('executive') ||
    roleStr.includes('admin');
  // Treat users with explicit "super" in role or common superadmin flags as super admins.
  // Assumption: superadmin may appear as 'superadmin', 'super_admin', or contain 'super'.
  // Also allow optional userCtx flags if present (is_superadmin / is_super_admin).
  const isSuperAdmin =
    roleStr.includes('superadmin') 
    

  const [formData, setFormData] = useState({
    company_id: '',
    user_id: '',
    badge_number: '',
    phone_number: '',
    email: '',
    role_id: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    icard_no: '',
    dgp_id: '',
    designation_name: '',
    request_type: 'leave related',
     appointment_date: '',
    appointment_time: '12:00:00',
    notes: '',
    auto_scheduled: false,
    is_leave_appointment: true,
    status: 'scheduled', // Default status for new appointments

    // Simple leave fields matching backend
    leave_type: '',
    leave_start_date: '',
    leave_end_date: '',
    leave_reason: '',

    // Clerk intake fields (optional, mainly for updates at desk)
    date_of_joining: '',
    rewards: '',
    punishments: '',
    remaining_al: '0',
    remaining_pl: 0,
    // Keep original PL string (e.g. "5/5") for display in UI
    remaining_pl_text: '',
    sick: 0,
    absent: '0',
    sick_hajar: 0,
    sick_gairhajar: 0,

    // Remarks
    clerk_remarks: '',
    commandant_remarks: ''
  });

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');

  const queryClient = useQueryClient();

  // Leave type mapping for WhatsApp appointments
  const leaveTypeMapping = {
    'leave_sick': 'गैरहजर मधून हजर होणे',
    'leave_absent': 'सिक् मधून हजर होणे',
    'leave_deputation': 'प्रतिनियुक्ती वरून हजर होणे',
    'bed_rest': 'Bed Rest'
  };

  // Fetch companies
  const { data: companies, isLoading: companiesLoading } = useCompanies();

  // Add users query
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await usersAPI.getAll();
        return response.data.data || [];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Server-side search as-you-type
  const { data: searchedUsers, isFetching: searchedUsersFetching } = useQuery({
    queryKey: ['users', 'search', userSearchQuery?.toLowerCase()?.trim()],
    enabled: !!userSearchQuery && userSearchQuery.trim().length >= 2,
    queryFn: async () => {
      try {
        const response = await usersAPI.getAll({ search: userSearchQuery.trim().toLowerCase() });
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Error searching users:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => appointmentsAPI.createLeaveAppointment(data),
    onSuccess: (res) => {
      const appt = res?.data?.appointment;
      if (appt) {
        setFormData(prev => ({
          ...prev,
          appointment_date: appt.appointment_date || prev.appointment_date,
          leave_start_date: appt.leave_start_date || prev.leave_start_date,
          leave_end_date: appt.leave_end_date || prev.leave_end_date,
          leave_type: appt.leave_type || prev.leave_type,
          leave_reason: appt.leave_reason || prev.leave_reason,
          date_of_joining: appt.date_of_joining || prev.date_of_joining,
          rewards: appt.rewards ?? prev.rewards,
          punishments: appt.punishments ?? prev.punishments,
          remaining_al: appt.remaining_al != null ? (typeof appt.remaining_al === 'number' ? appt.remaining_al : parseInt(appt.remaining_al, 10) || prev.remaining_al) : prev.remaining_al,
          remaining_pl: appt.remaining_pl != null ? (typeof appt.remaining_pl === 'number' ? appt.remaining_pl : parseInt(appt.remaining_pl, 10) || prev.remaining_pl) : prev.remaining_pl,
          remaining_pl_text: appt.remaining_pl != null ? String(appt.remaining_pl) : prev.remaining_pl_text,
          sick: typeof appt.sick === 'number' ? appt.sick : prev.sick,
          absent: appt.absent != null ? String(appt.absent) : prev.absent,
          sick_hajar: typeof appt.sick_hajar === 'number' ? appt.sick_hajar : prev.sick_hajar,
          sick_gairhajar: typeof appt.sick_gairhajar === 'number' ? appt.sick_gairhajar : prev.sick_gairhajar,
          clerk_remarks: appt.clerk_remarks ?? prev.clerk_remarks,
          commandant_remarks: appt.commandant_remarks ?? prev.commandant_remarks
        }));
      }
      queryClient.invalidateQueries(['appointments']);
      onSuccess?.();
      onClose?.();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsAPI.updateLeaveAppointment(id, data),
    onSuccess: (res) => {
      const appt = res?.data?.appointment;
      if (appt) {
        setFormData(prev => ({
          ...prev,
          appointment_date: appt.appointment_date || prev.appointment_date,
          leave_start_date: appt.leave_start_date || prev.leave_start_date,
          leave_end_date: appt.leave_end_date || prev.leave_end_date,
          leave_type: appt.leave_type || prev.leave_type,
          leave_reason: appt.leave_reason || prev.leave_reason,
          date_of_joining: appt.date_of_joining || prev.date_of_joining,
          rewards: appt.rewards ?? prev.rewards,
          punishments: appt.punishments ?? prev.punishments,
          remaining_al: appt.remaining_al != null ? (typeof appt.remaining_al === 'number' ? appt.remaining_al : parseInt(appt.remaining_al, 10) || prev.remaining_al) : prev.remaining_al,
          remaining_pl: appt.remaining_pl != null ? (typeof appt.remaining_pl === 'number' ? appt.remaining_pl : parseInt(appt.remaining_pl, 10) || prev.remaining_pl) : prev.remaining_pl,
          remaining_pl_text: appt.remaining_pl != null ? String(appt.remaining_pl) : prev.remaining_pl_text,
          sick: typeof appt.sick === 'number' ? appt.sick : prev.sick,
          absent: appt.absent != null ? String(appt.absent) : prev.absent,
          sick_hajar: typeof appt.sick_hajar === 'number' ? appt.sick_hajar : prev.sick_hajar,
          sick_gairhajar: typeof appt.sick_gairhajar === 'number' ? appt.sick_gairhajar : prev.sick_gairhajar,
          clerk_remarks: appt.clerk_remarks ?? prev.clerk_remarks,
          officer_remarks: appt.officer_remarks ?? prev.officer_remarks,
          commandant_remarks: appt.commandant_remarks ?? prev.commandant_remarks,
        }));
      }
      queryClient.invalidateQueries(['appointments']);
      onSuccess?.();
      onClose?.();
    },
  });

  // Effect to populate form when editing
  useEffect(() => {
    if (editAppointment) {
      setFormData({
        // Map only the fields we use in the simplified schema
        company_id: editAppointment.company_id || '',
        user_id: editAppointment.user_id || '',
        badge_number: editAppointment.badge_number || '',
        phone_number: formatPhoneForDisplay(editAppointment.phone_number) || '',
        email: editAppointment.email || '',
        role_id: editAppointment.role_id || '',
        first_name: editAppointment.first_name || '',
        middle_name: editAppointment.middle_name || '',
        last_name: editAppointment.last_name || '',
        icard_no: editAppointment.icard_no || '',
        dgp_id: editAppointment.dgp_id || '',
        designation_name: editAppointment.designation_name || '',
        request_type: editAppointment.request_type || 'leave related',
        appointment_date: editAppointment.appointment_date || '',
        appointment_time: editAppointment.appointment_time || '12:00:00',
        notes: editAppointment.notes || '',
        is_leave_appointment: true,
        status: 'completed' || editAppointment.status, // Default to 'completed' when editing

        leave_type: editAppointment.leave_type || '',
        leave_start_date: editAppointment.leave_start_date || '',
        leave_end_date: editAppointment.leave_end_date || '',
        leave_reason: editAppointment.leave_reason || '',

        date_of_joining: editAppointment.date_of_joining || '',
        rewards: editAppointment.rewards || '',
        punishments: editAppointment.punishments || '',
        remaining_al: editAppointment.remaining_al != null ? String(editAppointment.remaining_al) : '0',
        remaining_pl: editAppointment.remaining_pl != null ? (typeof editAppointment.remaining_pl === 'number' ? editAppointment.remaining_pl : parseInt(editAppointment.remaining_pl, 10) || 0) : 0,
        remaining_pl_text: editAppointment.remaining_pl != null ? String(editAppointment.remaining_pl) : '',
        sick: typeof editAppointment.sick === 'number' ? editAppointment.sick : 0,
        absent: editAppointment.absent != null ? String(editAppointment.absent) : '0',
        sick_hajar: typeof editAppointment.sick_hajar === 'number' ? editAppointment.sick_hajar : 0,
        sick_gairhajar: typeof editAppointment.sick_gairhajar === 'number' ? editAppointment.sick_gairhajar : 0,

        clerk_remarks: editAppointment.clerk_remarks || '',
        commandant_remarks: editAppointment.commandant_remarks || ''
      });
      setSelectedUserName(editAppointment.full_name || `${editAppointment.first_name} ${editAppointment.last_name}`);
      setUserSearchQuery(editAppointment.full_name || `${editAppointment.first_name} ${editAppointment.last_name}`);
    }
  }, [editAppointment]);

  // Prefill from current user when creating new
  useEffect(() => {
    if (!editAppointment && userCtx) {
      setFormData(prev => ({
        ...prev,
        company_id: userCtx.company_id || prev.company_id,
        user_id: userCtx.id || prev.user_id,
        badge_number: userCtx.badge_no || userCtx.badge_number || prev.badge_number,
        phone_number: formatPhoneForDisplay(userCtx.phone_number) || prev.phone_number,
        email: userCtx.email || prev.email,
        role_id: userCtx.role_id || prev.role_id,
        first_name: userCtx.first_name || prev.first_name,
        middle_name: userCtx.middle_name || prev.middle_name,
        last_name: userCtx.last_name || prev.last_name,
        icard_no: userCtx.icard_no || prev.icard_no,
        dgp_id: userCtx.dgp_id || prev.dgp_id,
        designation_name: userCtx.designation_name || prev.designation_name,

      }));
      const name = `${userCtx.first_name || ''} ${userCtx.last_name || ''}`.trim();
      setSelectedUserName(name);
      setUserSearchQuery(name);
    }
  }, [userCtx, editAppointment]);

  // No leave_details in simplified flow

  // Helper function to calculate total leave days (counting all days including weekends and holidays)
  const calculateTotalLeaveDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    // Fix timezone issue by forcing local time
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    
    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    if (start > end) return 0;
    
    let totalDays = 0;
    const currentDate = new Date(start);
    
    // Count each and every day from start to end (inclusive)
    // Includes weekends (Saturday, Sunday) and holidays
    while (currentDate <= end) {
      totalDays++;
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return totalDays;
  };

  // Calculate total leave days using useMemo for performance
  const totalLeaveDays = useMemo(() => {
    if (formData.leave_start_date && formData.leave_end_date) {
      return calculateTotalLeaveDays(
        formData.leave_start_date,
        formData.leave_end_date
      );
    }
    return 0;
  }, [formData.leave_start_date, formData.leave_end_date]);

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.company_id || !formData.user_id || !formData.leave_type || !formData.leave_start_date || !formData.leave_end_date || !formData.leave_reason || !formData.date_of_joining) {
      alert('Please fill required fields: Company, User, Leave Type, Start/End Dates, Reason, Date of Joining');
      return;
    }

    // Prepare payload and coerce numeric fields for backend validation
    const toInt = (v) => {
      if (v === '' || v === null || v === undefined) return undefined;
      const parsed = parseInt(v, 10);
      return isNaN(parsed) ? undefined : parsed;
    };
    
    const submitData = { ...formData };
    
    // Ensure required numeric fields are properly converted
    submitData.company_id = toInt(formData.company_id);
    submitData.user_id = toInt(formData.user_id);
    
    // Handle badge_number - can be null/undefined for backend
    if (formData.badge_number !== undefined && formData.badge_number !== null && formData.badge_number !== '') {
      submitData.badge_number = toInt(formData.badge_number);
    } else {
      submitData.badge_number = null; // Explicitly set to null for backend
    }
    
    // Ensure clerk intake fields are properly converted
    submitData.remaining_al = formData.remaining_al != null ? String(formData.remaining_al) : '0'; // Allow text format like "5/6"
    // remaining_pl is stored/validated as text in backend, but we still keep numeric state for internal logic
    submitData.remaining_pl =
      formData.remaining_pl_text != null && String(formData.remaining_pl_text).trim() !== ''
        ? String(formData.remaining_pl_text)
        : String(toInt(formData.remaining_pl) ?? 0);
    submitData.sick = toInt(formData.sick) ?? 0;
    submitData.absent = formData.absent != null ? String(formData.absent) : '0'; // Allow text format like "5/6"
    submitData.sick_hajar = toInt(formData.sick_hajar) ?? 0;
    submitData.sick_gairhajar = toInt(formData.sick_gairhajar) ?? 0;
    
    // Ensure request_type is set correctly
    submitData.request_type = 'leave related';
    
    // Ensure is_leave_appointment is true
    submitData.is_leave_appointment = true;

    // Ensure status is included from form dropdown
    submitData.status = formData.status || 'scheduled';

    // Include remarks fields when creating new appointment
    if (!editAppointment) {
      submitData.clerk_remarks = formData.clerk_remarks || '';
      submitData.commandant_remarks = formData.commandant_remarks || '';
    }

    try {
      if (editAppointment) {
        // Only send the remark relevant to the user's role (backend stamps processed_by/at)
        const roleRemark = isClerk
          ? { clerk_remarks: formData.clerk_remarks }
          : isOfficer
            ? { officer_remarks: formData.officer_remarks }
            : isCommandant
              ? { commandant_remarks: formData.commandant_remarks }
              : {};

        await updateMutation.mutateAsync({
          id: editAppointment.id,
          data: { ...submitData, ...roleRemark }
        });
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error) {
      console.error('Error submitting leave appointment:', error);
      
      // Handle specific validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        if (errorData.details) {
          // Multiple field validation errors
          const errorMessages = Object.entries(errorData.details)
            .map(([field, message]) => `${field}: ${message}`)
            .join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else if (errorData.field && errorData.message) {
          // Single field validation error
          alert(`Validation error: ${errorData.message}`);
        } else {
          // Generic error message
          alert(`Error: ${errorData.message}`);
        }
      } else {
        // Fallback for network errors or other issues
        alert('Error submitting leave appointment. Please try again.');
      }
    }
  };

  // Add getFilteredUsers function
  const getFilteredUsers = () => {
    const query = userSearchQuery?.trim();
    if (!query) return [];
    
    const q = query.toLowerCase();
    
    // Use server results when query length >= 2 and server search has results
    if (query.length >= 2 && Array.isArray(searchedUsers) && searchedUsers.length > 0) {
      return searchedUsers.slice(0, 8);
    }
    
    // Fall back to local search for queries < 2 chars or when server search has no results
    if (!Array.isArray(users) || users.length === 0) return [];
    
    return users.filter(user => {
      if (!user) return false;
      const searchFields = [
        user.first_name,
        user.last_name,
        user.full_name,
        user.email,
        user.phone_number,
        String(user.badge_number || user.badge_no || '')
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchFields.includes(q);
    }).slice(0, 8);
  };

  // Update handleUserSearchChange
  const handleUserSearchChange = (e) => {
    const query = e.target.value;
    setUserSearchQuery(query);
    // Always show suggestions when typing
    setShowUserSuggestions(true);

    if (query.length === 0) {
      setFormData(prev => ({
        ...prev,
        user_id: '',
        company_id: '',
        badge_number: '',
        phone_number: '',
        email: '',
        role_id: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        icard_no: '',
        dgp_id: '',
        designation_name: '',
      }));
      setSelectedUserName('');
    }
  };

  // Add handleUserSelect function
  const handleUserSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      user_id: user.id,
      company_id: user.company_id || '',
      badge_number: user.badge_no || user.badge_number || '',
      phone_number: formatPhoneForDisplay(user.phone_number) || '',
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      icard_no: user.icard_no || '',
      dgp_id: user.dgp_id || '',
      role_id: user.role_id || '',
      designation_name: user.designation_name || '',
    }));

    setSelectedUserName(user.full_name || `${user.first_name} ${user.last_name}`);
    setUserSearchQuery(user.full_name || `${user.first_name} ${user.last_name}`);
    setShowUserSuggestions(false);
  };

  // Function to handle Enter key press for user selection
  const handleUserSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filteredUsers = getFilteredUsers();
      if (filteredUsers.length > 0) {
        handleUserSelect(filteredUsers[0]);
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        <form id="leaveForm" onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <Card className="p-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Personal Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* User Search */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Search User
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={handleUserSearchChange}
                    onKeyDown={handleUserSearchKeyDown}
                    onFocus={() => {
                      if (userSearchQuery.trim().length > 0) {
                        setShowUserSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowUserSuggestions(false), 200)}
                    placeholder="Type user name to search..."
                    className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                    required
                  />
                  {showUserSuggestions && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {userSearchQuery.trim().length >= 2 && searchedUsersFetching ? (
                        <div className="px-4 py-3 text-gray-500">
                          Searching...
                        </div>
                      ) : (
                        <>
                          {getFilteredUsers().map((user) => (
                            <div
                              key={user.id}
                              onClick={() => handleUserSelect(user)}
                              className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email} • Badge: {user.badge_number || user.badge_no}
                              </div>
                            </div>
                          ))}
                          {getFilteredUsers().length === 0 && userSearchQuery.trim().length > 0 && (
                            <div className="px-4 py-3 text-gray-500">
                              {userSearchQuery.trim().length < 2 
                                ? 'Type at least 2 characters to search...' 
                                : 'No users found'}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </label>
                <input
                  value={formData.phone_number}
                  readOnly
                  placeholder="Phone number"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Company Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company
                </label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                  required
                >
                  <option value="">Select company...</option>
                  {!companiesLoading && companies?.data?.data?.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              {/* Badge Number */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Badge Number</label>
                <input
                  value={formData.badge_number}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Email</label>
                <input
                  value={formData.email}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Role */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Role</label>
                <input
                  value={formData.role_id}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* First Name */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">First Name</label>
                <input
                  value={formData.first_name}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Middle Name */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Middle Name</label>
                <input
                  value={formData.middle_name}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Last Name</label>
                <input
                  value={formData.last_name}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* I-Card Number */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">I-Card Number</label>
                <input
                  value={formData.icard_no}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* DGP ID */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">DGP ID</label>
                <input
                  value={formData.dgp_id}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Designation */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Designation</label>
                <input
                  value={formData.designation_name}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>
            </div>
          </Card>

          {/* Leave Details (simplified) */}
          <Card className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Leave Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  गैर हजरीचा प्रकार  (Type of Leave)
                </label>
                <Input
                  type="text"
                  value={leaveTypeMapping[formData.leave_type] || formData.leave_type}
                  onChange={(e) => {
                    // If it's a mapped value, find the key; otherwise use the value directly
                    const mappedKey = Object.keys(leaveTypeMapping).find(key => leaveTypeMapping[key] === e.target.value);
                    setFormData(prev => ({ ...prev, leave_type: mappedKey || e.target.value }));
                  }}
                  className="w-full font-dvtt-input"
                  placeholder="e.g., Sick Leave, Casual Leave"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  रजेचा प्रारंभ दिनांक (Start Date)
                </label>
                <Input
                  type="date"
                  value={formData.leave_start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, leave_start_date: e.target.value }))}
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  हजर होण्याची दिनांक (End Date)
                </label>
                <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={formData.leave_end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, leave_end_date: e.target.value }))}
                  className="w-full"
                  required
                />
                  {formData.leave_start_date && formData.leave_end_date && totalLeaveDays > 0 && (
                    <div className="px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg min-w-[120px] text-center">
                      <p className="text-xs font-medium text-teal-800 dark:text-teal-200">
                        रजेचे एकूण दिवस (Total Leave Days)
                      </p>
                      <p className="text-xl font-bold text-teal-900 dark:text-teal-100">
                        {totalLeaveDays}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  रजेचे कारण (Reason for Leave)
                </label>
                <textarea
                  value={formData.leave_reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, leave_reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white font-dvtt-input"
                  rows={3}
                  placeholder="रजेचे कारण लिहा... (Enter reason for leave...)"
                  required
                />
              </div>
            </div>
          </Card>

          {/* <Card className="p-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">भेट तपशील (Appointment Details)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  हजर होण्यासाठी भेट दिनांक (Appointment Date)
                </label>
                <Input
                  type="date"
                  value={formData.appointment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_date: e.target.value }))}
                  className="w-full"
                  min={new Date().toISOString().split('T')[0]} // Allow same day or future dates only
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  आजची तारीख किंवा भविष्यातील तारीख निवडा (Select today's date or a future date)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  भेट वेळ (Appointment Time)
                </label>
                <Input
                  type="time"
                  value={formData.appointment_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_time: e.target.value }))}
                  className="w-full"
                  required
                />
              </div>
            </div>
          </Card> */}

          {/* Clerk Intake (optional, mainly during updates at desk) */}
          <Card className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">क्लर्क प्रविष्टी (Clerk Intake - Desk Use)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">भरती दिनांक (Date of Joining)</label>
                <Input
                  type="date"
                  value={formData.date_of_joining}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_of_joining: e.target.value }))}
                  className="w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">शिल्लक अर्जित रजा (Remaining AL)</label>
                <Input
                  type="text"
                  value={formData.remaining_al}
                  onChange={(e) => setFormData(prev => ({ ...prev, remaining_al: e.target.value }))}
                  className="w-full"
                  placeholder="e.g., 5/6 or 15"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">शिल्लक परिवर्तित रजा (Remaining PL)</label>
                <Input
                  type="text"
                  value={formData.remaining_pl_text}
                  onChange={(e) => {
                    const nextText = e.target.value;
                    const nextNum = parseInt(nextText || '0', 10);
                    setFormData(prev => ({
                      ...prev,
                      remaining_pl_text: nextText,
                      remaining_pl: isNaN(nextNum) ? 0 : nextNum,
                    }));
                  }}
                  className="w-full"
                  placeholder="e.g., 5/5 or 15"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">पूर्वीचा सेवा अभिलेख सिक (Sick Days)</label>
                <Input
                  type="number"
                  value={formData.sick}
                  onChange={(e) => setFormData(prev => ({ ...prev, sick: parseInt(e.target.value || '0', 10) }))}
                  className="w-full"
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">गैरहजर (Absent Days)</label>
                <Input
                  type="text"
                  value={formData.absent}
                  onChange={(e) => setFormData(prev => ({ ...prev, absent: e.target.value }))}
                  className="w-full"
                  placeholder="e.g., 5/6 or 2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">सिक हजर (Sick Hajar)</label>
                <Input
                  type="number"
                  value={formData.sick_hajar}
                  onChange={(e) => setFormData(prev => ({ ...prev, sick_hajar: parseInt(e.target.value || '0', 10) }))}
                  className="w-full"
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">सिक गैरहजर (Sick Gairhajar)</label>
                <Input
                  type="number"
                  value={formData.sick_gairhajar}
                  onChange={(e) => setFormData(prev => ({ ...prev, sick_gairhajar: parseInt(e.target.value || '0', 10) }))}
                  className="w-full"
                  min={0}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">बक्षीस (Rewards)</label>
                <textarea
                  value={formData.rewards}
                  onChange={(e) => setFormData(prev => ({ ...prev, rewards: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white font-dvtt-input"
                  rows={2}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">शिक्षा (Punishments)</label>
                <textarea
                  value={formData.punishments}
                  onChange={(e) => setFormData(prev => ({ ...prev, punishments: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white font-dvtt-input"
                  rows={2}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Clerk's Remarks</label>
                <div className="font-dvtt-input">
                  <RichTextEditor
                    value={formData.clerk_remarks}
                    onChange={(content) => setFormData(prev => ({ ...prev, clerk_remarks: content }))}
                    placeholder="Enter clerk's remarks..."
                  />
                </div>
              </div>
              {(isOfficer || formData.officer_remarks) && (
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Officer's Remarks</label>
                  <RichTextEditor
                    value={formData.officer_remarks || ''}
                    onChange={(content) => setFormData(prev => ({ ...prev, officer_remarks: content }))}
                    placeholder="Enter officer's remarks..."
                    disabled={!isOfficer}
                  />
                </div>
              )}
              {(isCommandant || formData.commandant_remarks || isSuperAdmin) && (
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Commandant's Remarks</label>
                  <div className="">
                    <RichTextEditor
                      value={formData.commandant_remarks || ''}
                      onChange={(content) => setFormData(prev => ({ ...prev, commandant_remarks: content }))}
                      placeholder="Only superadmin can enter commandant's remarks."
                      // Only superadmin can edit commandant remarks. Other users see the field but it is disabled.
                      disabled={!isSuperAdmin}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Submit Buttons and Status */}
          <div className="flex items-center justify-between pt-4 border-t">
            {/* Status Dropdown */}
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                required
              >
                <option value="scheduled">📅 Scheduled</option>
                <option value="clerk_processed">📝 Clerk Processed</option>
                <option value="completed">✅ Completed</option>
                <option value="cancelled">❌ Cancelled</option>
                <option value="rescheduled">🔄 Rescheduled</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex items-center space-x-2"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{editAppointment ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : (
                  <span>{editAppointment ? 'Update Leave Application' : 'Submit Leave Application'}</span>
                )}
              </Button>
            </div>
          </div>

        </form>
      </div>
    </div>
  );
};

export default LeaveAppointmentForm;